import { AgsMatrixFactory } from "ags/setup/factories/ags-matrix-factory";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { AlternatingBlankReelSpinnerResultBuffer } from "slotworks/components/matrix/reel/transition-behaviours/spin/alternating-blanks-reel-spinner-result-buffer";
import { ReelSpinner, SpinStageEvents } from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import { SpinReelTransition } from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import { ThemeWinCyclePulseSymbolBehaviour } from "behaviours/theme-win-cycle-pulse-symbol-behaviour";
import { Components } from "appworks/components/components";
import { ThemeCelebrateWinComponent } from "components/theme-celebrate-win-component";
import { ThemeTintMultiplierSymbolBehaviour } from "behaviours/theme-tint-multiplier-symbol-behavior";
import { ThemeFiniteAnimationBehaviour } from "behaviours/theme-finite-animation-behaviour";
import { ThemeBorderHighlightBehaviour } from "behaviours/theme-border-highlight-behaviour";


export class ThemeMatrixFactory extends AgsMatrixFactory {

    constructor() {
        super();

        this.spinStages = [
            {
                name: "accelerate",
                s: -1,
                u: 0,
                v: -this.spinSpeed,
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 50
                    }
                ]
            },
            {
                name: "minRepeat",
                s: -1,
                u: - this.spinSpeed,
                v: - this.spinSpeed
            },
            {
                name: "repeat",
                s: -1,
                u: -this.spinSpeed,
                v: -this.spinSpeed
            },
            {
                name: "stop",
                s: -this.minStopDistance,
                u: -this.spinSpeed,
                v: -this.spinSpeed,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 200
                    },
                    {
                        name: SpinStageEvents.ANTICIPATE,
                        delay: 375
                    }
                ]
            },
            {
                name: "land",
                s: 0,
                t: 0
            },
            {
                name: "fullstop",
                s: -0.35,
                u: -0.01,
                v: 0
            },
            {
                name: "complete",
                s: 0.35,
                t: 220,
                easing: "Quadratic.Out",
                events: [
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 0
                    }
                ]
            }
        ];

        this.skipStages = [
            {
                name: "stop",
                s: -this.minStopDistance,
                u: -this.spinSpeed,
                v: -this.spinSpeed,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 0
                    },
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 0
                    },
                    {
                        name: SpinStageEvents.ANTICIPATE,
                        delay: 550
                    }
                ]
            },
            {
                name: "land",
                s: 0,
                t: 0
            },
            {
                name: "fullstopskip",
                s: -0.5,
                u: -this.spinSpeed,
                v: 0
            },
            {
                name: "complete",
                s: 0.5,
                t: 200
            }
        ];

        this.anticipationStages = [
            {
                name: "tease",
                s: -34,
                u: -this.spinSpeed,
                v: -this.spinSpeed
            },
            {
                name: "stop",
                s: -this.minStopDistance,
                u: -this.spinSpeed,
                v: -0.03
            },
            {
                name: "anticipate",
                s: -this.minStopDistance,
                u: -this.spinSpeed,
                v: -0.03,
            },
            {
                name: "land",
                s: 0,
                t: 0
            },
            {
                name: "fullstop",
                s: -0.5,
                u: -this.spinSpeed,
                v: 0
            },
            {
                name: "complete",
                s: 0.5,
                t: 150,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 150
                    },
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 0
                    },
                    {
                        name: SpinStageEvents.ANTICIPATE,
                        delay: 50
                    }
                ]
            }
        ];

        this.quickSpinStages = [
            {
                name: "accelerate",
                s: -1,
                u: 0,
                v: -this.spinSpeed * 2,
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 50
                    }
                ]
            },
            {
                name: "minRepeat",
                s: -1,
                u: -this.spinSpeed * 2,
                v: -this.spinSpeed * 2
            },
            {
                name: "repeat",
                s: -1,
                u: -this.spinSpeed * 2,
                v: -this.spinSpeed * 2,
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 0
                    }
                ]
            },
            {
                name: "stop",
                s: -this.minStopDistance,
                u: -this.spinSpeed * 2,
                v: -this.spinSpeed * 2,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 0
                    },
                    {
                        name: SpinStageEvents.ANTICIPATE,
                        delay: 375
                    }
                ]
            },
            {
                name: "land",
                s: 0,
                t: 0
            },
            {
                name: "overshoot",
                s: -0.35,
                u: -0.01,
                v: 0
            },
            {
                name: "complete",
                s: 0.35,
                t: 220,
                easing: "Quadratic.Out",
                events: [
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 100
                    }
                ]
            }
        ];
    }

    protected createTransition(matrix: AbstractMatrixComponent) {

        ReelSpinner.addStageSet({
            id: "default",
            spin: [this.spinStages],
            skip: [this.skipStages],
            anticipate: [this.anticipationStages],
            quickSpin: [this.quickSpinStages]
        });

        ReelSpinner.setStageSet("default");
        ReelSpinner.bufferType = AlternatingBlankReelSpinnerResultBuffer;
        matrix.setTransition(new SpinReelTransition());
    }

    protected addSymbolBehaviours(matrix: AbstractMatrixComponent) {
        matrix.addDefaultSymbolBehaviour((symbol) => new ThemeTintMultiplierSymbolBehaviour(symbol));
        matrix.addDefaultSymbolBehaviour((symbol) => new ThemeWinCyclePulseSymbolBehaviour(symbol, Components.get(ThemeCelebrateWinComponent).config.lineWaitTime),null,["BO"]);
        matrix.addDefaultSymbolBehaviour((symbol) => new ThemeFiniteAnimationBehaviour(symbol), ["X2", "X3","BO"]);
        matrix.addDefaultSymbolBehaviour((symbol) => new ThemeBorderHighlightBehaviour(symbol),["BO"]);
    }
}
